/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as ActionTypes from "constants/ActionTypes";
import { BlockTypes } from "constants/BlockTypes";
import { updateIfExists } from "reducers/utilities/UpdateIfExists";
import { ClipUploadStates } from "constants/ClipUploadStates";
import {
  Clip,
  ClipEffect,
  isClipTypeWithoutAssetSupport,
  isClipWithAsset,
} from "types/Clip";
import { ClipsAction } from "types/Actions";

function replaceClips(
  clips: Clip[],
  clipsToReplace: Clip[],
  clipsToInsert: Clip[]
): Clip[] {
  if (clipsToReplace.length === 0) {
    return [...clips, ...clipsToInsert];
  }

  const startIdx = clips.findIndex((_, clipsIdx) => {
    return clipsToReplace.every((_, sublistIdx) => {
      return clips[clipsIdx + sublistIdx].id === clipsToReplace[sublistIdx].id;
    });
  });

  if (startIdx !== -1) {
    const endIdx = startIdx + clipsToReplace.length;

    return [
      ...clips.slice(0, startIdx),
      ...clipsToInsert,
      ...clips.slice(endIdx),
    ];
  }

  return clips;
}

export default function ClipsReducer(
  clips: Clip[],
  action: ClipsAction
): Clip[] {
  switch (action.type) {
    case ActionTypes.LOAD_PROJECT:
      return action.clips;
    case ActionTypes.REPLACE_CLIPS:
      return replaceClips(clips, action.clipsToReplace, action.clipsToInsert);
    case ActionTypes.SET_CLIP_ESTIMATED_DURATION:
      return updateIfExists(clips, action.clipId, {
        duration: action.duration,
      });
    case ActionTypes.INSERT_ASSET:
      return updateIfExists(clips, action.clipId, {
        assetId: action.asset.id,
        duration: action.asset.duration,
        uploadState: ClipUploadStates.STARTED,
        type: BlockTypes.WAVEFORM, // If this clip was a placeholder, we now implicitly force it to be a waveform
        name: action.fileName, // And if it were a placeholder before, it didn't have a name
      });
    case ActionTypes.CLIP_UPLOADED:
      return updateIfExists(clips, action.clipId, {
        uploadState: ClipUploadStates.COMPLETE,
      });
    case ActionTypes.TRANSCRIBE_ASSET_SUCCESS:
      return updateIfExists(
        clips,
        action.assetId,
        (clip) => ({
          type: BlockTypes.TRANSCRIPTION,
          tokenIdPrefix: clip.id,
        }),
        (item: Clip): string | undefined =>
          (isClipWithAsset(item) && item.assetId) || undefined
      );
    case ActionTypes.SET_CLIP_IS_BACKGROUND:
      return updateIfExists(clips, action.clipId, {
        isBackground: action.isBackground,
      });
    case ActionTypes.ASSET_UPLOAD_FAILED:
      return clips.filter(
        (clip) =>
          isClipTypeWithoutAssetSupport(clip) ||
          (isClipWithAsset(clip) && clip.assetId !== action.assetId)
      );
    case ActionTypes.RECORDING_UPLOAD_FAILED:
      return updateIfExists(clips, action.clipId, {
        uploadState: ClipUploadStates.FAILED,
      });
    case ActionTypes.RECORDING_STARTING:
      return updateIfExists(clips, action.clipId, {
        type: BlockTypes.RECORDING_SESSION,
        uploadState: ClipUploadStates.PENDING,
      });
    case ActionTypes.IMPORT_RECORDING_SESSION_INTO_PLACEHOLDER: // Used to "convert" a placeholder clip into a RECORDING_SESSION clip
      return updateIfExists(clips, action.clipId, {
        type: BlockTypes.RECORDING_SESSION,
        uploadState: ClipUploadStates.PROCESSING,
      });
    case ActionTypes.UPDATE_RECORDING_DURATION:
      return updateIfExists(clips, action.clipId, {
        duration: action.duration,
      });
    case ActionTypes.SET_PLACEHOLDER_TEXT:
      return updateIfExists(clips, action.clipId, { text: action.text });
    case ActionTypes.RECORDING_SESSION_PROCESSING_REQUEST:
      return updateIfExists(clips, action.clipId, {
        uploadState: ClipUploadStates.STARTED,
      });
    case ActionTypes.RECORDING_SESSION_PROCESSING_SUCCEEDED:
      return updateIfExists(clips, action.clipId, {
        uploadState: ClipUploadStates.COMPLETE,
        assetId: action.asset.id,
        duration: action.duration,
      });
    case ActionTypes.RECORDING_SESSION_PROCESSING_STARTED:
      return updateIfExists(clips, action.clipId, {
        uploadState: ClipUploadStates.PROCESSING,
      });
    case ActionTypes.TOGGLE_EFFECT: // TODO: Test
      const effectName: ClipEffect = action.effectName;
      switch (effectName) {
        case ClipEffect.fadeIn: {
          const clipId = action.block.clipIds[0];
          return clips.map((clip) => {
            if (clip.type !== BlockTypes.WAVEFORM || clip.id !== clipId) {
              return clip;
            }
            return {
              ...clip,
              fadeIn: !Boolean(clip.fadeIn),
            };
          });
        }
        case ClipEffect.fadeOut: {
          const clipId = action.block.clipIds[action.block.clipIds.length - 1];
          return clips.map((clip) => {
            if (clip.type !== BlockTypes.WAVEFORM || clip.id !== clipId) {
              return clip;
            }
            return {
              ...clip,
              fadeOut: !Boolean(clip.fadeOut),
            };
          });
        }
        default:
          return clips;
      }
    case ActionTypes.ASSET_PROCESSING_FAILED:
      return clips.filter(
        (clip) => isClipWithAsset(clip) && clip.assetId !== action.assetId
      );
    case ActionTypes.TRANSCRIBE_ASSET_FAILURE:
      return updateIfExists(
        clips,
        action.assetId,
        {
          type: BlockTypes.WAVEFORM,
          fadeIn: false,
          fadeOut: false,
          isBackground: false,
        },
        (item: Clip): string | undefined =>
          isClipWithAsset(item) ? item.assetId : undefined
      );
    default:
      return clips;
  }
}
